import { Switch, Route, Redirect, useLocation, useSearch } from "wouter";
import { useTranslation } from "react-i18next";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useIntercom } from "@/hooks/useIntercom";
import { usePostHog } from "@/hooks/usePostHog";
import { useStore } from "@/lib/store";
import { useState, useEffect, useRef, lazy, Suspense, type ComponentType } from "react";
import { canAccessSitesModule, standardAllowedModules } from "@shared/module-access";
import { ThemeProvider } from "@/components/theme-provider";
import KeyboardShortcutsOverlay from "@/components/keyboard-shortcuts-overlay";
import { AppShellWithSidebar } from "@/components/layout/AppShellWithSidebar";
import { getPendingShopifyClaimToken } from "@/lib/shopify-claim-storage";
import ChatPage from "@/pages/chat";
import AuthPage from "@/pages/auth";
import { apiRequest } from "@/lib/queryClient";

// Heavy admin / superadmin pages — code-split so non-admin users never load them.
const lazyPage = <P,>(loader: () => Promise<{ default: ComponentType<P> }>) => lazy(loader);
const AdminPage = lazyPage(() => import("@/pages/admin"));
const AdminDashboardPage = lazyPage(() => import("@/pages/admin-dashboard"));
const AdminCostsPage = lazyPage(() => import("@/pages/admin-costs"));
const AgencyAdminPage = lazyPage(() => import("@/pages/agency-admin"));
const ChangelogAdminPage = lazyPage(() => import("@/pages/changelog-admin"));
const ShopifyClaimPage = lazyPage(() => import("@/pages/shopify-claim"));
const SHOPIFY_CLAIM_PATH = "/integracje/shopify/claim";
import ScalingUpCockpitPage from "@/pages/superadmin/scaling-up-cockpit";
import AutopilotHubPage from "@/pages/autopilot/index";
import AutopilotWizardPage from "@/pages/autopilot/wizard";
import AutopilotDashboardPage from "@/pages/autopilot/dashboard";
import AutopilotAdminPage from "@/pages/autopilot/admin";
import AutopilotInspirationPage from "@/pages/autopilot/inspiration";
import AutopilotPipelinePage from "@/pages/autopilot/pipeline";
import FirmAssetsLibraryPage from "@/pages/firm-assets/index";
import ScalingUpVisionPage from "@/pages/superadmin/scaling-up-vision";
import ScalingUpKpisPage from "@/pages/superadmin/scaling-up-kpis";
import ScalingUpMeetingsPage from "@/pages/superadmin/scaling-up-meetings";
import ScalingUpActionItemsPage from "@/pages/superadmin/scaling-up-action-items";
import ScalingUpIssuesPage from "@/pages/superadmin/scaling-up-issues";
import ScalingUpHoldingsPage from "@/pages/superadmin/scaling-up-holdings";
import ScalingUpPeoplePage from "@/pages/superadmin/scaling-up-people";
import ManagerDashboardPage from "@/pages/manager-dashboard";
import DashboardPage from "@/pages/dashboard";
import { ResetPasswordPage } from "@/pages/reset-password";
import SetupAccountPage from "@/pages/setup-account";
import AcceptInvitePage from "@/pages/accept-invite";
import IntegrationsHubPage from "@/pages/integrations-hub";
import YoutubeAnalyticsPage from "@/pages/youtube-analytics";
import TrialPage from "@/pages/trial";
import OrganicPlanPage from "@/pages/organic-plan";
import OrganicSalesPage from "@/pages/organic-sales";
import CheckoutPage from "@/pages/checkout";
import PartnerPage from "@/pages/partner";
import SalesPage from "@/pages/sales";
import ThankYouPage from "@/pages/thank-you";
import SettingsPage from "@/pages/settings";
import AttributionOverridesPage from "@/pages/attribution-overrides";
import IntegrationsGuidePage from "@/pages/integrations-guide";
import { TrialBanner } from "@/components/trial-banner";
import { PaymentFailedBanner } from "@/components/payment-failed-banner";
import { OnboardingProgressBar } from "@/components/onboarding-progress-bar";
import MonitoringPage from "@/pages/monitoring";
import LeadsPage from "@/pages/leads";
import TagsPage from "@/pages/tags";
import EcommerceCostsPage from "@/pages/ecommerce-costs";
import CustomerJourneyPage from "@/pages/customer-journey";
import ViralGalleryPage from "@/pages/viral-gallery";
import SitesPage from "@/pages/sites";
import NotFound from "@/pages/not-found";
import { CreditCard, Lock } from "lucide-react";

function TrialExpiredGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const { t } = useTranslation();
  const accessBlocked = (user as any)?.accessBlocked === true;
  const isPaymentIssue = (user as any)?.paymentFailedAt != null;

  if (!accessBlocked) return <>{children}</>;

  // Allow /settings so they can pay
  if (location === "/settings") return <>{children}</>;

  const titleKey = isPaymentIssue ? "paymentGate.title" : "trial.expired";
  const descKey = isPaymentIssue ? "paymentGate.description" : "trial.expiredDescription";
  const ctaKey = isPaymentIssue ? "paymentGate.cta" : "trial.choosePlan";

  return (
    <div className="min-h-screen bg-[#030407] flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
          <Lock className="w-8 h-8 text-red-400" />
        </div>
        <div className="space-y-2">
          <h1 className="text-heading-2 font-extrabold text-white">{t(titleKey)}</h1>
          <p className="text-white/60 text-sm leading-relaxed">{t(descKey)}</p>
        </div>
        <button
          onClick={() => setLocation("/settings")}
          className="w-full bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-700 hover:to-orange-600 text-white font-semibold py-3 px-6 rounded-lg flex items-center justify-center gap-2 transition-all"
        >
          <CreditCard className="w-5 h-5" />
          {t(ctaKey)}
        </button>
      </div>
    </div>
  );
}

function Router() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { setModules, modules } = useStore();
  const [location, setLocation] = useLocation();
  const search = useSearch();

  // VP-255: shop-first claim Shopify — strona claim wysyła na login/rejestrację,
  // ale nic nie wracało potem na stronę claim, więc sklep zostawał niepodpięty
  // (udowodnione sesją recenzenta App Store).
  //
  // VP-308: wracamy pod adres z `?next=`, czyli Z TOKENEM. Powrót na goły
  // /integracje/shopify/claim zostawiał localStorage jedynym nośnikiem tokenu, a ten
  // bywa niedostępny (Safari Private, iframe panelu Shopify). localStorage zostaje
  // wyłącznie jako zapas.
  //
  // `next` zatrzaskujemy w renderze, w którym się pojawił: dla zalogowanego usera
  // `/` renderuje <Redirect>, a jego efekt biegnie PRZED efektem routera i zdążyłby
  // zgubić query. Akceptujemy wyłącznie własną ścieżkę claimu (brak open-redirect).
  const claimNextRef = useRef<string | null>(null);
  const nextParam = new URLSearchParams(search).get("next");
  if (nextParam && nextParam.startsWith(SHOPIFY_CLAIM_PATH)) {
    claimNextRef.current = nextParam;
  }

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (location.startsWith(SHOPIFY_CLAIM_PATH)) return;
    const next = claimNextRef.current;
    if (next) {
      // Konsumujemy jednorazowo i przez `replace` — inaczej wpis `/?next=…` zostaje
      // w historii i „wstecz" po udanym claimie wpycha stronę claim jeszcze raz,
      // już ze zużytym tokenem.
      claimNextRef.current = null;
      setLocation(next, { replace: true });
      return;
    }
    if (getPendingShopifyClaimToken()) setLocation(SHOPIFY_CLAIM_PATH);
  }, [isLoading, isAuthenticated, location, setLocation]);

  // Fetch modules globally — sidebar is populated on every page, not just /chat.
  const { data: fetchedModules } = useQuery({
    queryKey: ["/api/modules"],
    queryFn: () => apiRequest("GET", "/api/modules"),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
  useEffect(() => {
    if (fetchedModules && Array.isArray(fetchedModules)) {
      setModules(fetchedModules);
    }
  }, [fetchedModules, setModules]);

  // Safety fallback: if auth check takes >15s, stop showing loading spinner.
  // The queryClient has a 20s fetch timeout, but this ensures the UI unblocks
  // even if something slips through.
  const [authTimedOut, setAuthTimedOut] = useState(false);
  useEffect(() => {
    if (!isLoading) {
      setAuthTimedOut(false);
      return;
    }
    const timerId = setTimeout(() => setAuthTimedOut(true), 15000);
    return () => clearTimeout(timerId);
  }, [isLoading]);

  const showLoading = isLoading && !authTimedOut;

  // Check if user is dashboard-only (based on allowedModules, not role).
  // Opt-in modules (e.g. 'sites') are stripped first — see shared/module-access.ts.
  const allowedModules = standardAllowedModules((user as any)?.allowedModules ?? null);
  const userRole: string = (user as any)?.role ?? "client";
  const isDashboardOnly =
    Array.isArray(allowedModules) &&
    allowedModules.length === 1 &&
    allowedModules[0] === "dashboard";
  const isManagerOnly = userRole === "manager";
  // Dostęp do modułu "Strony" — super_admin lub jawny wpis 'sites' w allowed_modules
  // (ten sam helper co backendowy middleware requireSitesAccess).
  const isSitesAllowed = canAccessSitesModule(
    user as { role?: string | null; allowedModules?: string[] | null } | null
  );
  // VP-116: trasy Performance (dashboard/KPI, leady, atrybucja, koszty e-commerce).
  // Backend zwraca na nie 403 dla samego Organica, więc bez tego user zobaczyłby
  // pustą stronę z błędami zamiast czytelnego przekierowania.
  //
  // Flaga pochodzi z /api/auth/me i jest rozwiązana serwerowo (dziedziczenie po
  // właścicielu agencji). Parsowanie `subscriptionPlanKey` tutaj dawało pracownikowi
  // agencji fail-open, bo jego własny klucz jest pusty. Brak pola = starszy payload
  // → nie blokujemy.
  const hasPerformance = (user as { hasPerformance?: boolean } | null)?.hasPerformance !== false;
  // Organic ma własny ekran startowy; /chat/home to powitanie trybu REKLAMY, którego
  // Organic nie może nawet włączyć (toggle ukryty).
  const performanceOnly = (Component: React.ComponentType) => () =>
    hasPerformance ? <Component /> : <Redirect to="/viralbooster" />;

  return (
    <Switch>
      {/* Public routes that don't require authentication */}
      <Route path="/reset-password" component={ResetPasswordPage} />
      <Route path="/accept-invite" component={AcceptInvitePage} />
      <Route path="/setup-account/:email" component={SetupAccountPage} />
      <Route path="/trial" component={TrialPage} />
      <Route path="/organic-plan" component={OrganicPlanPage} />
      <Route path="/organic" component={OrganicSalesPage} />
      <Route path="/register" component={TrialPage} />
      <Route path="/checkout" component={CheckoutPage} />
      {/* Shopify shop-first claim — accessible in both authed and anonymous state. */}
      <Route path={SHOPIFY_CLAIM_PATH}>
        {() => (
          <Suspense
            fallback={
              <div className="min-h-screen bg-[#030407] flex items-center justify-center text-white/60">
                Wczytywanie…
              </div>
            }
          >
            <ShopifyClaimPage />
          </Suspense>
        )}
      </Route>

      {/* Handle loading state */}
      {showLoading ? (
        <Route>
          <div className="min-h-screen bg-[#030407] flex items-center justify-center">
            <div className="text-white text-lg">{t("common.loading")}</div>
          </div>
        </Route>
      ) : !isAuthenticated ? (
        <Route component={AuthPage} />
      ) : isDashboardOnly ? (
        <TrialExpiredGate>
          <Switch>
            {/* Dashboard-only users: only dashboard route, everything else redirects */}
            <Route path="/dashboard">{performanceOnly(DashboardPage)}</Route>
            <Route path="/integracje" component={IntegrationsHubPage} />
            <Route path="/integracje/przewodnik" component={IntegrationsGuidePage} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/attribution-overrides">{performanceOnly(AttributionOverridesPage)}</Route>
            <Route path="/monitoring">{performanceOnly(MonitoringPage)}</Route>
            <Route path="/leads">{performanceOnly(LeadsPage)}</Route>
            {/* VP-105: ViralBooster — ORGANIC agent home */}
            <Route path="/viralbooster" component={ChatPage} />
            <Route path="/tags">{performanceOnly(TagsPage)}</Route>
            <Route path="/ecommerce-costs">{performanceOnly(EcommerceCostsPage)}</Route>
            <Route path="/customer-journey">{performanceOnly(CustomerJourneyPage)}</Route>
            <Route>
              {/* VP-116: Organic bez Performance nie moze byc wpychany na dashboard,
                  ktory zwraca mu 403 — to konfiguracja sprzeczna, ale nie zostawiamy
                  usera na martwym ekranie. */}
              <Redirect to={hasPerformance ? "/dashboard" : "/viralbooster"} />
            </Route>
          </Switch>
        </TrialExpiredGate>
      ) : isManagerOnly ? (
        <TrialExpiredGate>
          <Switch>
            {/* Managers: manager dashboard + regular dashboard, everything else redirects */}
            <Route path="/moiklienci">{performanceOnly(ManagerDashboardPage)}</Route>
            <Route path="/dashboard">{performanceOnly(DashboardPage)}</Route>
            <Route path="/integracje" component={IntegrationsHubPage} />
            <Route path="/integracje/przewodnik" component={IntegrationsGuidePage} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/attribution-overrides">{performanceOnly(AttributionOverridesPage)}</Route>
            <Route path="/monitoring">{performanceOnly(MonitoringPage)}</Route>
            <Route path="/leads">{performanceOnly(LeadsPage)}</Route>
            {/* VP-105: ViralBooster — ORGANIC agent home */}
            <Route path="/viralbooster" component={ChatPage} />
            <Route path="/tags">{performanceOnly(TagsPage)}</Route>
            <Route path="/ecommerce-costs">{performanceOnly(EcommerceCostsPage)}</Route>
            <Route path="/customer-journey">{performanceOnly(CustomerJourneyPage)}</Route>
            <Route>
              <Redirect to="/moiklienci" />
            </Route>
          </Switch>
        </TrialExpiredGate>
      ) : (
        <TrialExpiredGate>
          <Suspense
            fallback={
              <div className="min-h-screen bg-[#030407] flex items-center justify-center">
                <div className="text-white text-lg">{t("common.loading")}</div>
              </div>
            }
          >
            <Switch>
              {/* Full-access authenticated routes - default to offer builder */}
              <Route path="/">
                <Redirect to="/chat/home" />
              </Route>
              {/* VP-105: ViralBooster — ORGANIC agent home */}
              <Route path="/viralbooster" component={ChatPage} />
              <Route path="/chat" component={ChatPage} />
              <Route path="/chat/:moduleId" component={ChatPage} />
              <Route path="/chat/:moduleId/:conversationId" component={ChatPage} />
              <Route path="/dashboard">{performanceOnly(DashboardPage)}</Route>
              <Route path="/integracje" component={IntegrationsHubPage} />
              <Route path="/integracje/przewodnik" component={IntegrationsGuidePage} />
              <Route path="/youtube-analytics" component={YoutubeAnalyticsPage} />
              <Route path="/admin" component={AdminPage} />
              <Route path="/admin/dashboard" component={AdminDashboardPage} />
              <Route path="/admin/costs" component={AdminCostsPage} />
              <Route path="/admin/scaling-up" component={ScalingUpCockpitPage} />
              <Route path="/admin/autopilot">
                {() => (
                  <AppShellWithSidebar>
                    <AutopilotHubPage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/admin/autopilot/new">
                {() => (
                  <AppShellWithSidebar>
                    <AutopilotWizardPage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/admin/autopilot/triage">
                {() => (
                  <AppShellWithSidebar>
                    <AutopilotAdminPage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/admin/autopilot/inspiration">
                {() => (
                  <AppShellWithSidebar>
                    <AutopilotInspirationPage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/admin/autopilot/pipeline">
                {() => (
                  <AppShellWithSidebar>
                    <AutopilotPipelinePage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/admin/autopilot/:id">
                {() => (
                  <AppShellWithSidebar>
                    <AutopilotDashboardPage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/firm-assets">
                {() => (
                  <AppShellWithSidebar>
                    <FirmAssetsLibraryPage />
                  </AppShellWithSidebar>
                )}
              </Route>
              <Route path="/admin/scaling-up/vision" component={ScalingUpVisionPage} />
              <Route path="/admin/scaling-up/kpis" component={ScalingUpKpisPage} />
              <Route path="/admin/scaling-up/meetings" component={ScalingUpMeetingsPage} />
              <Route path="/admin/scaling-up/action-items" component={ScalingUpActionItemsPage} />
              <Route path="/admin/scaling-up/issues" component={ScalingUpIssuesPage} />
              <Route path="/admin/scaling-up/holdings" component={ScalingUpHoldingsPage} />
              <Route path="/admin/scaling-up/people" component={ScalingUpPeoplePage} />
              <Route path="/changelog-admin" component={ChangelogAdminPage} />
              <Route path="/agency-admin" component={AgencyAdminPage} />
              <Route path="/settings" component={SettingsPage} />
              <Route path="/attribution-overrides">
                {performanceOnly(AttributionOverridesPage)}
              </Route>
              <Route path="/moiklienci">{performanceOnly(ManagerDashboardPage)}</Route>
              <Route path="/leads">{performanceOnly(LeadsPage)}</Route>
              <Route path="/tags">{performanceOnly(TagsPage)}</Route>
              <Route path="/ecommerce-costs">{performanceOnly(EcommerceCostsPage)}</Route>
              <Route path="/customer-journey">{performanceOnly(CustomerJourneyPage)}</Route>
              <Route path="/viral" component={ViralGalleryPage} />
              {isSitesAllowed && <Route path="/strony" component={SitesPage} />}
              <Route path="/monitoring">
                <Redirect to="/chat/monitoring" />
              </Route>
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </TrialExpiredGate>
      )}
    </Switch>
  );
}

function ImpersonationBanner() {
  const { setAuthToken } = useStore();
  const originalToken = localStorage.getItem("gawronify-original-owner-token");
  if (!originalToken) return null;

  const handleRestore = () => {
    setAuthToken(originalToken);
    localStorage.removeItem("gawronify-original-owner-token");
    // Return to the panel we came from (super_admin → /admin, agency_owner → /agency-admin).
    const origin = localStorage.getItem("gawronify-impersonation-origin") || "/agency-admin";
    localStorage.removeItem("gawronify-impersonation-origin");
    queryClient.clear();
    window.location.href = origin;
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-[#ef3e29] text-white text-center py-1.5 px-4 text-sm flex items-center justify-center gap-3">
      <span>Testujesz jako użytkownik</span>
      <button
        onClick={handleRestore}
        className="bg-white/20 hover:bg-white/30 px-3 py-0.5 rounded text-xs font-semibold transition-colors"
      >
        ← Wróć do panelu admina
      </button>
    </div>
  );
}

function AppContent() {
  useIntercom(); // Initialize Intercom
  usePostHog(); // Initialize PostHog
  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <ImpersonationBanner />
      <TrialBanner />
      <PaymentFailedBanner />
      <OnboardingProgressBar />
      <Toaster />
      <SonnerToaster richColors position="top-right" />
      <KeyboardShortcutsOverlay />
      <div className="flex-1 min-h-0">
        <Router />
      </div>
    </div>
  );
}

function App() {
  const [location] = useLocation();
  const isPartnerPage = location.startsWith("/partner");
  const isSalesPage = location.startsWith("/lp");

  if (isPartnerPage) {
    return (
      <Switch>
        <Route path="/partner/:name" component={PartnerPage} />
        <Route path="/partner" component={PartnerPage} />
      </Switch>
    );
  }

  if (isSalesPage) {
    return (
      <Switch>
        <Route path="/lp/thank-you" component={ThankYouPage} />
        <Route path="/lp/ads" component={SalesPage} />
        <Route path="/lp/:variant" component={SalesPage} />
        <Route path="/lp" component={SalesPage} />
      </Switch>
    );
  }

  return <AppContent />;
}

function AppRoot() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="dark">
        <App />
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default AppRoot;
